import { useState, useRef, useCallback } from 'react';

/**
 * Tracks carousel navigation direction and which button was last clicked.
 *
 * direction:        1 = forward (right), -1 = backward (left)
 * clickedButton:   'next' | 'prev' | 'dot' | null
 * directionSwitched: true when the queued nav is opposite to the in-flight nav
 *
 * Designed for AnimatePresence mode="wait". Queues one direction-switch nav
 * attempted during animation so it executes immediately after completion.
 */
const useCarouselDirection = (initialIndex = 0, length = 0) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [direction, setDirection] = useState(1);
  const [clickedButton, setClickedButton] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [directionSwitched, setDirectionSwitched] = useState(false);

  const lengthRef = useRef(length);
  const isAnimatingRef = useRef(false);
  const navActiveRef = useRef(false);
  const startCountRef = useRef(0);
  const inFlightDirectionRef = useRef(1); // direction of the currently animating nav
  const pendingNavRef = useRef(null);     // { newIndex, newDirection, button } queued during animation
  lengthRef.current = length;

  const commitNav = useCallback((newIndex, newDirection, button) => {
    const switched = newDirection !== inFlightDirectionRef.current;
    inFlightDirectionRef.current = newDirection;

    navActiveRef.current = true;
    startCountRef.current = 0;
    isAnimatingRef.current = true;

    setDirectionSwitched(switched);
    setIsAnimating(true);
    setDirection(newDirection);
    setClickedButton(button);
    setCurrentIndex(newIndex);

  }, []);

  const next = useCallback(() => {
    const newIndex = currentIndex === lengthRef.current - 1 ? 0 : currentIndex + 1;
    if (isAnimatingRef.current) {
      if (inFlightDirectionRef.current !== 1) {
        pendingNavRef.current = { newIndex, newDirection: 1, button: 'next' };
      }
      return;
    }
    commitNav(newIndex, 1, 'next');
  }, [currentIndex, commitNav]);

  const prev = useCallback(() => {
    const newIndex = currentIndex === 0 ? lengthRef.current - 1 : currentIndex - 1;
    if (isAnimatingRef.current) {
      if (inFlightDirectionRef.current !== -1) {
        pendingNavRef.current = { newIndex, newDirection: -1, button: 'prev' };
      }
      return;
    }
    commitNav(newIndex, -1, 'prev');
  }, [currentIndex, commitNav]);

  const goTo = useCallback((index) => {
    if (index === currentIndex) return;
    const newDirection = index > currentIndex ? 1 : -1;
    if (isAnimatingRef.current) {
      if (inFlightDirectionRef.current !== newDirection) {
        pendingNavRef.current = { newIndex: index, newDirection, button: 'dot' };
      }
      return;
    }
    commitNav(index, newDirection, 'dot');
  }, [currentIndex, commitNav]);

  const onAnimationStart = useCallback(() => {
    if (!navActiveRef.current) return;
    startCountRef.current += 1;
  }, []);

  const onAnimationComplete = useCallback(() => {
    if (!navActiveRef.current) return;
    if (startCountRef.current >= 2) {
      navActiveRef.current = false;
      startCountRef.current = 0;
      isAnimatingRef.current = false;
      setIsAnimating(false);

      // Execute any queued direction-switch navigation
      if (pendingNavRef.current) {
        const { newIndex, newDirection, button } = pendingNavRef.current;
        pendingNavRef.current = null;
        commitNav(newIndex, newDirection, button);
      }
    }
  }, [commitNav]);

  return {
    currentIndex,
    direction,
    clickedButton,
    directionSwitched,
    isAnimating,
    next,
    prev,
    goTo,
    onAnimationStart,
    onAnimationComplete,
  };
};

export default useCarouselDirection;
